/**
 * Timezone utility functions for handling nursery-specific timezone conversions
 * All dates from the API are in UTC format and need to be converted to nursery timezone
 */

import { TIMEZONE_OPTIONS } from './constants';

export const getDetectedTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const formatTimezoneLabel = (tz: string): string => {
  try {
    const now = new Date();
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = now.toLocaleString('en-US', { timeZone: tz });
    const utcDate = new Date(utcStr);
    const tzDate = new Date(tzStr);
    const diffMs = tzDate.getTime() - utcDate.getTime();
    const sign = diffMs >= 0 ? '+' : '-';
    const absDiff = Math.abs(diffMs);
    const hours = Math.floor(absDiff / 3600000);
    const minutes = Math.floor((absDiff % 3600000) / 60000);
    const offset = minutes > 0
      ? `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
      : `UTC${sign}${String(hours).padStart(2, '0')}:00`;
    return `${tz} (${offset})`;
  } catch {
    return tz;
  }
};

let cachedTimezoneOptions: { value: string; label: string }[] | null = null;

export const getTimezoneOptions = (): { value: string; label: string }[] => {
  if (cachedTimezoneOptions) return cachedTimezoneOptions;

  const detected = getDetectedTimezone();
  let timezones: string[] = [];

  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      timezones = intl.supportedValuesOf('timeZone');
    }
  } catch {
    // fallback
  }

  if (timezones.length === 0) {
    const fallback = [...TIMEZONE_OPTIONS].sort((a, b) => {
      if (a.value === detected) return -1;
      if (b.value === detected) return 1;
      return a.value.localeCompare(b.value);
    });
    cachedTimezoneOptions = fallback;
    return fallback;
  }

  if (!timezones.includes('UTC')) {
    timezones = ['UTC', ...timezones];
  }

  const options = timezones.map((tz) => ({
    value: tz,
    label: formatTimezoneLabel(tz)
  }));

  options.sort((a, b) => {
    if (a.value === detected) return -1;
    if (b.value === detected) return 1;
    return a.value.localeCompare(b.value);
  });

  cachedTimezoneOptions = options;
  return options;
};

/**
 * Convert UTC date string to nursery timezone
 * @param utcDateString - UTC date string from API (e.g., "2025-08-18T14:30:00.000000Z")
 * @param nurseryTimezone - Nursery timezone (e.g., "Asia/Dubai")
 * @returns Date object in nursery timezone
 */
export const convertUTCToNurseryTimezone = (utcDateString: string, nurseryTimezone: string): Date => {
  if (!utcDateString) return new Date();
  
  try {
    // Create UTC date object
    const utcDate = new Date(utcDateString);
    
    // Convert to nursery timezone
    const nurseryDate = new Date(utcDate.toLocaleString('en-US', { timeZone: nurseryTimezone }));
    
    // Calculate timezone offset to preserve the actual time
    const utcTime = utcDate.getTime();
    const nurseryTime = nurseryDate.getTime();
    const offset = nurseryTime - utcTime;
    
    // Apply offset to get correct time in nursery timezone
    return new Date(utcTime + offset);
  } catch (error) {
    console.error('Error converting UTC to nursery timezone:', error);
    // Fallback to original date if conversion fails
    return new Date(utcDateString);
  }
};

/**
 * Format date in nursery timezone
 * @param utcDateString - UTC date string from API
 * @param nurseryTimezone - Nursery timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string in nursery timezone
 */
export const formatDateInNurseryTimezone = (
  utcDateString: string, 
  nurseryTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';
  
  try {
    const nurseryDate = convertUTCToNurseryTimezone(utcDateString, nurseryTimezone);
    
    // Default options for date formatting
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    
    return nurseryDate.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date in nursery timezone:', error);
    return 'N/A';
  }
};


const normalizeToUTC = (dateString: string): string => {
  const s = dateString.trim().replace(' ', 'T');
  if (s.endsWith('Z')) return s;
  if (/[+-]\d{2}:\d{2}$/.test(s)) return s;
  return s + 'Z';
};

/**
 * Format date and time in nursery timezone
 * @param utcDateString - UTC date string from API
 * @param nurseryTimezone - Nursery timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date and time string in nursery timezone
 */
export const formatDateTimeInNurseryTimezone = (
  utcDateString: string,
  nurseryTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';

  try {
    const normalized = normalizeToUTC(utcDateString);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: nurseryTimezone,
      ...options,
    };


    return new Date(normalized).toLocaleString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date time in nursery timezone:', error);
    return 'N/A';
  }
};

/**
 * Format time only in nursery timezone
 * @param utcDateString - UTC date string from API
 * @param nurseryTimezone - Nursery timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string in nursery timezone
 */
export const formatTimeInNurseryTimezone = (
  utcDateString: string, 
  nurseryTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';
  
  try {
    const nurseryDate = convertUTCToNurseryTimezone(utcDateString, nurseryTimezone);
    
    // Default options for time formatting
    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return nurseryDate.toLocaleTimeString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting time in nursery timezone:', error);
    return 'N/A';
  }
};

/**
 * Get today's date in YYYY-MM-DD format for nursery timezone
 * @param nurseryTimezone - Nursery timezone
 * @returns Date string in YYYY-MM-DD format
 */
export const getTodayDateInNurseryTimezone = (nurseryTimezone: string): string => {
  try {
    const now = new Date();
    const nurseryDate = new Date(now.toLocaleString('en-US', { timeZone: nurseryTimezone }));
    
    const year = nurseryDate.getFullYear();
    const month = String(nurseryDate.getMonth() + 1).padStart(2, '0');
    const day = String(nurseryDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error getting today date in nursery timezone:', error);
    // Fallback to local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
};

/**
 * Get current month in YYYY-MM format for nursery timezone
 * @param nurseryTimezone - Nursery timezone
 * @returns Month string in YYYY-MM format
 */
export const getCurrentMonthInNurseryTimezone = (nurseryTimezone: string): string => {
  try {
    const now = new Date();
    const nurseryDate = new Date(now.toLocaleString('en-US', { timeZone: nurseryTimezone }));
    
    const year = nurseryDate.getFullYear();
    const month = String(nurseryDate.getMonth() + 1).padStart(2, '0');
    
    return `${year}-${month}`;
  } catch (error) {
    console.error('Error getting current month in nursery timezone:', error);
    // Fallback to local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
};

/**
 * Check if a date is today in nursery timezone
 * @param utcDateString - UTC date string from API
 * @param nurseryTimezone - Nursery timezone
 * @returns boolean indicating if the date is today
 */
export const isTodayInNurseryTimezone = (utcDateString: string, nurseryTimezone: string): boolean => {
  if (!utcDateString) return false;
  
  try {
    const nurseryDate = convertUTCToNurseryTimezone(utcDateString, nurseryTimezone);
    const today = new Date();
    const todayInNurseryTZ = new Date(today.toLocaleString('en-US', { timeZone: nurseryTimezone }));
    
    return (
      nurseryDate.getDate() === todayInNurseryTZ.getDate() &&
      nurseryDate.getMonth() === todayInNurseryTZ.getMonth() &&
      nurseryDate.getFullYear() === todayInNurseryTZ.getFullYear()
    );
  } catch (error) {
    console.error('Error checking if date is today in nursery timezone:', error);
    return false;
  }
};


